/**
 * Concept Application Utilities
 * Handles applying concept assignments to plays
 * Resolves selectors and creates drawings on the play canvas
 */

import { resolveSelector } from './SelectorResolution'
import type { SelectorOptions } from './SelectorResolution'
import type { Player } from '@/components/canvas/BaseCanvas.types'
import type { Drawing } from '@/types'
import type { Concept, ConceptAssignment, ConceptSide, PlayDirection, RelativeSelector, TargetingMode } from '@/types/Concept.types'

export interface ConceptApplicationResult {
	success: boolean
	appliedDrawings: Drawing[]
	errors: string[]
	warnings: string[]
}

/**
 * Apply a concept to a play by resolving assignments and creating drawings
 * @param concept The concept with player assignments
 * @param players The current players in the play (formation)
 * @param existingDrawings Existing drawings on the play
 * @param callSide Side override from play call direction (overrides concept default_side)
 * @param formationStrength Which absolute side is "strong" for the formation
 * @returns Result with new drawings and any errors/warnings
 */
export function applyConceptToPlay(
	concept: Concept,
	players: Player[],
	existingDrawings: Drawing[],
	callSide?: ConceptSide | null,
	formationStrength?: 'left' | 'right',
): ConceptApplicationResult {
	const result: ConceptApplicationResult = {
		success: true,
		appliedDrawings: [],
		errors: [],
		warnings: [],
	}

	// Validate concept has assignments
	if (!concept.assignments || concept.assignments.length === 0) {
		result.warnings.push('Concept has no player assignments')
		return result
	}

	// Resolve effective side: call-level override takes precedence over concept default
	const effectiveSide = callSide ?? concept.default_side ?? null

	// Process each assignment
	for (const assignment of concept.assignments) {
		try {
			const drawing = applyAssignment(
				assignment,
				players,
				concept.targeting_mode,
				concept.play_direction,
				existingDrawings.length,
				effectiveSide,
				formationStrength,
			)

			if (drawing) {
				result.appliedDrawings.push(drawing)
			} else {
				result.warnings.push(
					`No player found for ${assignment.role || assignment.selector_type || 'unknown'}`,
				)
			}
		} catch (error) {
			result.success = false
			result.errors.push(
				`Failed to apply ${assignment.role || assignment.selector_type || 'unknown'}: ${error instanceof Error ? error.message : 'Unknown error'}`,
			)
		}
	}

	return result
}

/**
 * Apply a single assignment to create a drawing
 */
function applyAssignment(
	assignment: ConceptAssignment,
	players: Player[],
	targetingMode: TargetingMode,
	playDirection: PlayDirection,
	drawingIndexOffset: number,
	callSide?: ConceptSide | null,
	formationStrength?: 'left' | 'right',
): Drawing | null {
	let targetPlayer: Player | null = null

	// Resolve target player based on targeting mode
	if (targetingMode === 'absolute_role') {
		// Find player by role/label
		targetPlayer = players.find(p =>
			p.label?.toUpperCase() === assignment.role?.toUpperCase(),
		) || null
	} else if (targetingMode === 'relative_selector' && assignment.selector_type) {
		// Build side context for numbered selectors
		const selectorOptions: SelectorOptions = {
			callSide: callSide ?? undefined,
			formationStrength,
		}
		// Resolve using selector engine
		targetPlayer = resolveSelector(
			assignment.selector_type as RelativeSelector,
			{ players },
			playDirection,
			undefined,
			selectorOptions,
		)
	}

	if (!targetPlayer) {
		return null
	}

	// Deserialize drawing from assignment
	const drawing = deserializeDrawing(
		assignment.drawing_data,
		targetPlayer,
		drawingIndexOffset,
	)

	return drawing
}

/**
 * Deserialize drawing data and link it to a player
 */
function deserializeDrawing(
	drawingData: Drawing,
	player: Player,
	indexOffset: number,
): Drawing {
	// Create new drawing with unique ID
	const newDrawing: Drawing = {
		...drawingData,
		id: crypto.randomUUID(),
		playerId: player.id,
		linkedPointId: drawingData.linkedPointId || getFirstPointId(drawingData),
	}

	// Update linked point to player position
	if (newDrawing.linkedPointId) {
		const existingPoint = newDrawing.points[newDrawing.linkedPointId]
		if (existingPoint) {
			newDrawing.points = {
				...newDrawing.points,
				[newDrawing.linkedPointId]: {
					...existingPoint,
					x: player.x,
					y: player.y,
				},
			}
		}
	}

	return newDrawing
}

/**
 * Get the first point ID from a drawing (fallback for linkedPointId)
 */
function getFirstPointId(drawingData: Drawing): string | undefined {
	if (drawingData.segments && drawingData.segments.length > 0) {
		const firstSegment = drawingData.segments[0]
		if (firstSegment?.pointIds && firstSegment.pointIds.length > 0) {
			return firstSegment.pointIds[0]
		}
	}
	return undefined
}

/**
 * Validate that a formation can support a concept's requirements
 * @param concept The concept to validate
 * @param players The players in the formation
 * @returns Validation result with errors
 */
export function validateConceptForFormation(
	concept: Concept,
	players: Player[],
): { valid: boolean; errors: string[] } {
	const errors: string[] = []

	if (!concept.assignments || concept.assignments.length === 0) {
		return { valid: true, errors: [] } // No assignments = no requirements
	}

	// For Player Based targeting, check that all required roles exist
	if (concept.targeting_mode === 'absolute_role') {
		for (const assignment of concept.assignments) {
			const hasRole = players.some(p =>
				p.label?.toUpperCase() === assignment.role?.toUpperCase(),
			)

			if (!hasRole && assignment.role) {
				errors.push(`Formation missing required role: ${assignment.role}`)
			}
		}
	}

	// For Alignment Based targeting, validate play direction is set if needed
	if (concept.targeting_mode === 'relative_selector') {
		const requiresPlayDirection = concept.assignments.some(a =>
			a.selector_type?.includes('playside') || a.selector_type?.includes('backside'),
		)

		if (requiresPlayDirection && concept.play_direction === 'na') {
			errors.push('Concept requires play direction but none is set')
		}

	}

	return {
		valid: errors.length === 0,
		errors,
	}
}

/**
 * Get a summary of what roles/selectors a concept targets
 * Useful for displaying concept requirements
 */
export function getConceptTargets(concept: Concept): string[] {
	if (!concept.assignments) return []

	return concept.assignments.map(a => {
		if (concept.targeting_mode === 'absolute_role') {
			return a.role || 'Unknown'
		} else {
			return a.selector_type || 'Unknown selector'
		}
	}).filter((target): target is string => Boolean(target))
}
